import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type RefObject,
} from 'react'
import { useTranslation } from 'react-i18next'
import BuilderSegmentedControl from '../BuilderSegmentedControl'
import BuilderStepHead from './BuilderStepHead'
import type { DefectMark, DefectPart, DefectMarkType, DefectSeverity } from '../DefectMarkPad'
import {
  createDefectMark,
  deleteDefectMark,
  marksForPhotoId,
  migrateDefectMarks,
  sortDefectMarksByDisplayIndex,
  updateDefectMark,
} from '../../utils/defectMarks'
import {
  getContainImageRect,
  imagePercentToContainerPosition,
  pointerToImagePercent,
  type ContainImageRect,
} from '../../utils/defectAnnotationCoords'

export type BuilderPhotoSlot = 'front' | 'back' | 'side' | 'top' | 'bottom' | 'camera' | 'other'

export type BuilderPhotosStepPhoto = {
  id: string
  url: string
  file?: File
}

type Props = {
  photos: BuilderPhotosStepPhoto[]
  activePhotoId: string | null
  onActivePhotoIdChange: (id: string) => void
  marks: DefectMark[]
  onMarksChange: (value: DefectMark[] | ((prev: DefectMark[]) => DefectMark[])) => void
  selectedDefectId: string | null
  onSelectedDefectIdChange: (id: string | null) => void
  onOpenTutorial: () => void
  onAddPhotos: () => void
  onAddCamera: () => void
  photoPickError?: string
  dragActive?: boolean
  onDragEnter?: (e: DragEvent) => void
  onDragLeave?: (e: DragEvent) => void
  onDragOver?: (e: DragEvent) => void
  onDrop?: (e: DragEvent) => void
  canAddMore: boolean
  onRemovePhoto: (index: number) => void
  onEditPhoto: (index: number) => void
  onSetCover: (index: number) => void
  /** Recreate blob URL from `file` when preview fails to load. */
  onRefreshPhotoUrl?: (id: string) => void
  conditionUndocumentedCount?: number
  conditionUndocumentedMessage?: string
}

function useContainLayout(imageUrl: string | null, containerRef: RefObject<HTMLDivElement | null>) {
  const [natural, setNatural] = useState({ w: 0, h: 0 })
  const [box, setBox] = useState({ w: 0, h: 0 })

  useEffect(() => {
    if (!imageUrl) {
      setNatural({ w: 0, h: 0 })
      return
    }
    const img = new Image()
    img.onload = () => setNatural({ w: img.naturalWidth, h: img.naturalHeight })
    img.src = imageUrl
  }, [imageUrl])

  const syncBox = useCallback(() => {
    const el = containerRef.current
    if (!el) return
    setBox({ w: el.clientWidth, h: el.clientHeight })
  }, [containerRef])

  useLayoutEffect(() => {
    syncBox()
  }, [syncBox, imageUrl])

  useEffect(() => {
    const el = containerRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => syncBox())
    ro.observe(el)
    window.addEventListener('resize', syncBox)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', syncBox)
    }
  }, [syncBox, containerRef])

  const imageRect: ContainImageRect = useMemo(
    () => getContainImageRect(box.w, box.h, natural.w, natural.h),
    [box.w, box.h, natural.w, natural.h],
  )

  return { imageRect, containerWidth: box.w, containerHeight: box.h }
}

function IconTrash() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path d="M4 7h16M9 7V5h6v2M7 7l1 14h8l1-14" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

export default function BuilderPhotosStep({
  photos,
  activePhotoId,
  onActivePhotoIdChange,
  marks,
  onMarksChange,
  selectedDefectId,
  onSelectedDefectIdChange,
  onOpenTutorial,
  onAddPhotos,
  onAddCamera,
  photoPickError,
  dragActive = false,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDrop,
  canAddMore,
  onRemovePhoto,
  onEditPhoto,
  onSetCover,
  onRefreshPhotoUrl,
  conditionUndocumentedCount = 0,
  conditionUndocumentedMessage,
}: Props) {
  const { t } = useTranslation()
  const viewerRef = useRef<HTMLDivElement>(null)
  const refreshedPhotoUrlsRef = useRef<Set<string>>(new Set())
  const [markingMode, setMarkingMode] = useState(false)
  const dragMarkRef = useRef<{ id: string; pointerId: number } | null>(null)
  const [photoMenuId, setPhotoMenuId] = useState<string | null>(null)
  const [defectMenuId, setDefectMenuId] = useState<string | null>(null)

  const normalizedMarks = useMemo(() => migrateDefectMarks(marks, photos), [marks, photos])
  const activePhoto = photos.find((p) => p.id === activePhotoId) ?? photos[0] ?? null
  const activeIndex = activePhoto ? photos.findIndex((p) => p.id === activePhoto.id) : -1

  const { imageRect, containerWidth, containerHeight } = useContainLayout(activePhoto?.url ?? null, viewerRef)

  const photoDefects = activePhoto ? marksForPhotoId(normalizedMarks, activePhoto.id) : []
  const orderedAll = sortDefectMarksByDisplayIndex(normalizedMarks)
  const selectedDefect = orderedAll.find((m) => m.id === selectedDefectId) ?? null

  useEffect(() => {
    if (selectedDefectId && !normalizedMarks.some((m) => m.id === selectedDefectId)) {
      onSelectedDefectIdChange(null)
    }
  }, [normalizedMarks, selectedDefectId, onSelectedDefectIdChange])

  useEffect(() => {
    if (!photoMenuId && !defectMenuId) return
    const close = () => {
      setPhotoMenuId(null)
      setDefectMenuId(null)
    }
    window.addEventListener('click', close)
    return () => window.removeEventListener('click', close)
  }, [photoMenuId, defectMenuId])

  const typeOptions = useMemo(
    () =>
      (
        [
          ['scratch', 'listing.builder.defectType.scratch'],
          ['dent', 'listing.builder.defectType.dent'],
          ['crack', 'listing.builder.defectType.crack'],
          ['wear', 'listing.builder.defectType.wear'],
          ['other', 'listing.builder.defectType.other'],
        ] as const
      ).map(([value, key]) => ({ value, label: t(key) })),
    [t],
  )

  const severityOptions = useMemo(
    () =>
      (
        [
          ['minor', 'listing.builder.defectSeverity.minor'],
          ['visible', 'listing.builder.defectSeverity.visible'],
          ['significant', 'listing.builder.defectSeverity.significant'],
        ] as const
      ).map(([value, key]) => ({ value, label: t(key) })),
    [t],
  )

  const partOptions = useMemo(
    () =>
      (
        [
          ['screen', 'listing.builder.defectPart.screen'],
          ['frame', 'listing.builder.defectPart.frame'],
          ['back_glass', 'listing.builder.defectPart.backGlass'],
          ['camera', 'listing.builder.defectPart.camera'],
          ['other', 'listing.builder.defectPart.other'],
        ] as const
      ).map(([value, key]) => ({ value, label: t(key) })),
    [t],
  )

  function partLabel(part?: DefectPart) {
    const opt = partOptions.find((o) => o.value === part)
    return opt?.label ?? t('listing.builder.defectPart.frame')
  }

  function typeLabel(type?: DefectMarkType) {
    const opt = typeOptions.find((o) => o.value === type)
    return opt?.label ?? t('listing.builder.defectType.scratch')
  }

  function severityLabel(severity?: DefectSeverity) {
    const opt = severityOptions.find((o) => o.value === severity)
    return opt?.label ?? t('listing.builder.defectSeverity.minor')
  }

  function stepPhoto(delta: number) {
    if (photos.length === 0) return
    const next = (activeIndex + delta + photos.length) % photos.length
    onActivePhotoIdChange(photos[next].id)
  }

  function refreshPhotoUrlIfNeeded(photo: BuilderPhotosStepPhoto) {
    if (!photo.file || !onRefreshPhotoUrl) return
    if (refreshedPhotoUrlsRef.current.has(photo.id)) return
    refreshedPhotoUrlsRef.current.add(photo.id)
    onRefreshPhotoUrl(photo.id)
  }

  function handleViewerImgError() {
    if (!activePhoto) return
    refreshPhotoUrlIfNeeded(activePhoto)
  }

  function placeDefectAt(clientX: number, clientY: number) {
    if (!activePhoto || !viewerRef.current) return
    const rect = viewerRef.current.getBoundingClientRect()
    const pt = pointerToImagePercent(clientX, clientY, rect, imageRect)
    if (!pt) return
    onMarksChange((prev) => {
      const base = migrateDefectMarks(prev, photos)
      const next = createDefectMark(base, {
        photo_id: activePhoto.id,
        x: pt.x,
        y: pt.y,
      })
      const created = next[next.length - 1]
      if (created?.id) onSelectedDefectIdChange(created.id)
      return next
    })
    setMarkingMode(false)
  }

  function onViewerClick(e: React.MouseEvent<HTMLDivElement>) {
    if (!markingMode || dragMarkRef.current) return
    placeDefectAt(e.clientX, e.clientY)
  }

  function pinStyle(m: DefectMark) {
    if (containerWidth <= 0 || containerHeight <= 0) {
      return { left: `${m.x}%`, top: `${m.y}%` }
    }
    return imagePercentToContainerPosition(m.x, m.y, containerWidth, containerHeight, imageRect)
  }

  function selectDefect(mark: DefectMark) {
    if (!mark.id) return
    if (mark.photo_id) onActivePhotoIdChange(mark.photo_id)
    onSelectedDefectIdChange(mark.id)
  }

  function deleteDefectById(id: string) {
    onMarksChange((prev) => deleteDefectMark(migrateDefectMarks(prev, photos), id))
    if (selectedDefectId === id) onSelectedDefectIdChange(null)
    setDefectMenuId(null)
  }

  function toggleMarking() {
    if (!activePhoto) return
    setMarkingMode((m) => !m)
  }

  const uploadBlock = (
    <div
      className={`builder-photos-upload${dragActive ? ' builder-photos-upload--drag' : ''}`}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <p className="builder-photos-upload__title">{t('photoAssess.uploadCtaTitle')}</p>
      <p className="builder-photos-upload__note">{t('listing.photosOptional')}</p>
      <div className="builder-photos-upload__actions">
        <button type="button" className="device-photos-btn device-photos-btn--primary" disabled={!canAddMore} onClick={onAddPhotos}>
          {t('listing.devicePhotosPick')}
        </button>
        <button type="button" className="device-photos-btn" disabled={!canAddMore} onClick={onAddCamera}>
          {t('listing.devicePhotosCamera')}
        </button>
      </div>
      {photoPickError ? <p className="error-text">{photoPickError}</p> : null}
    </div>
  )

  const compactHead = (
    <BuilderStepHead title={t('listing.builder.photosSectionTitle')}>
      <button type="button" className="builder-photos-tutorial-link" onClick={onOpenTutorial}>
        {t('listing.builder.photoTutorialLink')}
      </button>
    </BuilderStepHead>
  )

  if (photos.length === 0) {
    return (
      <div className="builder-photos-step builder-photos-step--empty builder-step-surface builder-step-surface--flat">
        {compactHead}
        {uploadBlock}
      </div>
    )
  }

  return (
    <div className="builder-photos-step builder-step-surface builder-step-surface--flat">
      {compactHead}

      <div className="builder-photos-workspace">
        <div className="builder-photos-workspace__grid">
          <div className="builder-photos-workspace__left">
            <div
              ref={viewerRef}
              className={`builder-photos-viewer${markingMode ? ' builder-photos-viewer--marking' : ''}`}
              onClick={onViewerClick}
            >
              {activePhoto ? (
                <img
                  key={`${activePhoto.id}:${activePhoto.url}`}
                  src={activePhoto.url}
                  alt=""
                  draggable={false}
                  className="builder-photos-viewer__img"
                  onError={handleViewerImgError}
                />
              ) : null}
              {photoDefects.map((m) => {
                const n = m.displayIndex ?? 0
                const selected = m.id === selectedDefectId
                return (
                  <button
                    key={m.id ?? `${m.x}-${m.y}-${n}`}
                    type="button"
                    className={`builder-photos-pin${selected ? ' builder-photos-pin--selected' : ''}`}
                    style={pinStyle(m)}
                    onClick={(e) => {
                      e.stopPropagation()
                      if (m.id) selectDefect(m)
                    }}
                    onPointerDown={(e) => {
                      if (!m.id) return
                      e.stopPropagation()
                      dragMarkRef.current = { id: m.id, pointerId: e.pointerId }
                      ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
                    }}
                    onPointerMove={(e) => {
                      const drag = dragMarkRef.current
                      if (!drag || drag.id !== m.id || !viewerRef.current) return
                      const rect = viewerRef.current.getBoundingClientRect()
                      const pt = pointerToImagePercent(e.clientX, e.clientY, rect, imageRect)
                      if (!pt) return
                      onMarksChange((prev) => updateDefectMark(migrateDefectMarks(prev, photos), drag.id, { x: pt.x, y: pt.y }))
                    }}
                    onPointerUp={(e) => {
                      if (dragMarkRef.current?.pointerId === e.pointerId) dragMarkRef.current = null
                    }}
                    aria-label={t('listing.builder.defectPinAria', { n })}
                  >
                    {n}
                  </button>
                )
              })}
              {photos.length > 1 ? (
                <>
                  <button
                    type="button"
                    className="builder-photos-viewer__nav builder-photos-viewer__nav--prev"
                    onClick={() => stepPhoto(-1)}
                    aria-label={t('listing.defectMarkPhotoPrev')}
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    className="builder-photos-viewer__nav builder-photos-viewer__nav--next"
                    onClick={() => stepPhoto(1)}
                    aria-label={t('listing.defectMarkPhotoNext')}
                  >
                    ›
                  </button>
                </>
              ) : null}
              <span className="builder-photos-viewer__counter">
                {activeIndex + 1} / {photos.length}
              </span>
            </div>

            <div className="builder-photos-thumbs" role="tablist" aria-label={t('listing.devicePhotosLabel')}>
              {photos.map((photo, idx) => {
                const count = marksForPhotoId(normalizedMarks, photo.id).length
                const active = photo.id === activePhoto?.id
                const menuOpen = photoMenuId === photo.id
                return (
                  <div key={photo.id} className={`builder-photos-thumb${active ? ' builder-photos-thumb--active' : ''}`}>
                    <div className="builder-photos-thumb__media">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={active}
                        className="builder-photos-thumb__img-btn"
                        onClick={() => onActivePhotoIdChange(photo.id)}
                      >
                        <img
                          key={`${photo.id}:${photo.url}`}
                          src={photo.url}
                          alt=""
                          draggable={false}
                          onError={() => refreshPhotoUrlIfNeeded(photo)}
                        />
                        {idx === 0 ? (
                          <span className="builder-photos-thumb__cover-badge">{t('listing.builder.photoCoverBadge')}</span>
                        ) : null}
                        {count > 0 ? (
                          <span className="builder-photos-thumb__defect-badge" title={t('listing.builder.photoDefectCount', { count })}>
                            {count}
                          </span>
                        ) : null}
                      </button>
                      {idx !== 0 ? (
                        <button
                          type="button"
                          className="builder-photos-thumb__set-cover"
                          onClick={() => onSetCover(idx)}
                        >
                          {t('listing.photoSetCover')}
                        </button>
                      ) : null}
                    </div>
                    <div className="builder-photos-thumb__menu-wrap">
                      <button
                        type="button"
                        className="builder-photos-thumb__menu-btn"
                        aria-expanded={menuOpen}
                        aria-haspopup="menu"
                        onClick={(e) => {
                          e.stopPropagation()
                          setPhotoMenuId(menuOpen ? null : photo.id)
                          setDefectMenuId(null)
                        }}
                      >
                        •••
                      </button>
                      {menuOpen ? (
                        <div className="builder-photos-popover" role="menu" onClick={(e) => e.stopPropagation()}>
                          {idx !== 0 ? (
                            <button type="button" role="menuitem" onClick={() => { onSetCover(idx); setPhotoMenuId(null) }}>
                              {t('listing.photoSetCover')}
                            </button>
                          ) : null}
                          <button type="button" role="menuitem" onClick={() => { onEditPhoto(idx); setPhotoMenuId(null) }}>
                            {t('listing.builder.photoMenuCrop')}
                          </button>
                          <button type="button" role="menuitem" className="builder-photos-popover__danger" onClick={() => { onRemovePhoto(idx); setPhotoMenuId(null) }}>
                            {t('common.delete', 'Delete')}
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                )
              })}
              {canAddMore ? (
                <button type="button" className="builder-photos-thumb builder-photos-thumb--add" onClick={onAddPhotos}>
                  <span className="builder-photos-thumb__add-icon">+</span>
                  {t('listing.builder.addPhotosThumb')}
                </button>
              ) : null}
            </div>
          </div>

          <aside
            className={`builder-photos-inspector${orderedAll.length > 0 ? ' builder-photos-inspector--has-defects' : ''}${
              markingMode ? ' builder-photos-inspector--marking' : ''
            }`}
          >
            {conditionUndocumentedCount > 0 ? (
              <p className="builder-photos-inspector__warn" role="status">
                {t('listing.builder.conditionUndocumentedTitle', { count: conditionUndocumentedCount })}
                {' — '}
                {conditionUndocumentedMessage ?? t('listing.builder.conditionUndocumentedFallback')}
              </p>
            ) : null}

            <div className="builder-photos-inspector__list-section">
              <div className="builder-photos-inspector__list-head">
                <h4 className="builder-photos-inspector__heading">
                  {t('listing.builder.markedDefectsTitle', { count: orderedAll.length })}
                </h4>
                <button
                  type="button"
                  className={`builder-photos-inspector__mark${markingMode ? ' builder-photos-inspector__mark--active' : ''}`}
                  disabled={!activePhoto}
                  onClick={toggleMarking}
                >
                  {markingMode ? t('common.cancel') : t('listing.builder.markDefectShort')}
                </button>
              </div>
              <p
                className={`builder-photos-inspector__sub${markingMode ? ' builder-photos-inspector__sub--marking' : ''}`}
              >
                {markingMode
                  ? t('listing.builder.defectClickHint')
                  : t('listing.builder.markedDefectsLead')}
              </p>
              {orderedAll.length === 0 ? (
                <p className="builder-photos-inspector__empty">{t('listing.builder.noDefectsYet')}</p>
              ) : (
                <ul className="builder-photos-inspector__list">
                  {orderedAll.map((m) => {
                    const active = m.id === selectedDefectId
                    const photo = photos.find((p) => p.id === m.photo_id)
                    const menuOpen = defectMenuId === m.id
                    return (
                      <li key={m.id ?? `${m.displayIndex}`} className="builder-photos-inspector__list-item">
                        <div className={`builder-photos-defect-row${active ? ' builder-photos-defect-row--active' : ''}`}>
                          <button type="button" className="builder-photos-defect-row__main" onClick={() => selectDefect(m)}>
                            <span className="builder-photos-defect-row__thumb">{photo ? <img src={photo.url} alt="" /> : null}</span>
                            <span className="builder-photos-defect-row__num">{m.displayIndex}</span>
                            <span className="builder-photos-defect-row__body">
                              <span className="builder-photos-defect-row__part">{partLabel(m.part)}</span>
                              <span className="builder-photos-defect-row__desc">
                                {typeLabel(m.type)} · {severityLabel(m.severity)}
                              </span>
                            </span>
                          </button>
                          <button
                            type="button"
                            className="builder-photos-defect-row__menu-btn"
                            aria-label={t('common.moreActions', 'More actions')}
                            onClick={(e) => {
                              e.stopPropagation()
                              setDefectMenuId(menuOpen ? null : m.id!)
                              setPhotoMenuId(null)
                            }}
                          >
                            •••
                          </button>
                        </div>
                        {menuOpen && m.id ? (
                          <div className="builder-photos-popover builder-photos-popover--defect" onClick={(e) => e.stopPropagation()}>
                            <button type="button" className="builder-photos-popover__danger" onClick={() => deleteDefectById(m.id!)}>
                              {t('common.delete', 'Delete')}
                            </button>
                          </div>
                        ) : null}
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>

            <div className="builder-photos-inspector__editor">
              {selectedDefect?.id ? (
                <>
                  <label className="builder-photos-inspector__field builder-photos-inspector__field--part">
                    <span className="builder-photos-inspector__field-label-row">
                      <span>{t('listing.builder.defectPartLabel')}</span>
                      <button
                        type="button"
                        className="builder-photos-inspector__trash"
                        aria-label={t('common.delete', 'Delete')}
                        onClick={() => deleteDefectById(selectedDefect.id!)}
                      >
                        <IconTrash />
                      </button>
                    </span>
                    <select
                      className="builder-photos-inspector__select"
                      value={selectedDefect.part ?? 'frame'}
                      onChange={(e) =>
                        onMarksChange((prev) =>
                          updateDefectMark(migrateDefectMarks(prev, photos), selectedDefect.id!, {
                            part: e.target.value as DefectPart,
                          }),
                        )
                      }
                    >
                      {partOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <div className="builder-photos-inspector__segment-grid">
                    <div className="builder-photos-inspector__field builder-photos-inspector__field--compact">
                      <span>{t('listing.builder.defectTypeLabel')}</span>
                      <BuilderSegmentedControl
                        className="builder-defect-segmented builder-defect-segmented--type"
                        ariaLabel={t('listing.builder.defectTypeLabel')}
                        value={selectedDefect.type ?? 'scratch'}
                        options={typeOptions}
                        onChange={(v) =>
                          onMarksChange((prev) =>
                            updateDefectMark(migrateDefectMarks(prev, photos), selectedDefect.id!, {
                              type: v as DefectMarkType,
                            }),
                          )
                        }
                      />
                    </div>

                    <div className="builder-photos-inspector__field builder-photos-inspector__field--compact">
                      <span>{t('listing.builder.defectSeverityLabel')}</span>
                      <BuilderSegmentedControl
                        className="builder-defect-segmented builder-defect-segmented--severity"
                        ariaLabel={t('listing.builder.defectSeverityLabel')}
                        value={selectedDefect.severity ?? 'minor'}
                        options={severityOptions}
                        onChange={(v) =>
                          onMarksChange((prev) =>
                            updateDefectMark(migrateDefectMarks(prev, photos), selectedDefect.id!, {
                              severity: v as DefectSeverity,
                            }),
                          )
                        }
                      />
                    </div>
                  </div>

                  <label className="builder-photos-inspector__field builder-photos-inspector__field--desc">
                    <span>{t('listing.builder.defectDescriptionLabel')}</span>
                    <textarea
                      className="builder-photos-inspector__textarea"
                      maxLength={200}
                      rows={2}
                      value={selectedDefect.description ?? ''}
                      placeholder={t('listing.builder.defectDescriptionPlaceholder')}
                      onChange={(e) =>
                        onMarksChange((prev) =>
                          updateDefectMark(migrateDefectMarks(prev, photos), selectedDefect.id!, {
                            description: e.target.value,
                          }),
                        )
                      }
                    />
                    <span className="builder-photos-inspector__count">{(selectedDefect.description ?? '').length} / 200</span>
                  </label>
                </>
              ) : (
                <p className="builder-photos-inspector__editor-placeholder">{t('listing.builder.selectDefectHint')}</p>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  )
}
